import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Globe } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const AdminContentLanguages = () => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/content/languages');
      if (response.data.success) {
        // Transform is_active (0/1) to isActive (boolean)
        const transformedLanguages = (response.data.languages || []).map(lang => ({
          ...lang,
          isActive: lang.is_active === 1 || lang.is_active === true || lang.isActive === true
        }));
        setLanguages(transformedLanguages);
      }
    } catch (error) {
      toast.error('Failed to load languages');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedLanguage(null);
    setFormData({ name: '', code: '', isActive: true });
    setShowModal(true);
  };

  const openEditModal = (language) => {
    setSelectedLanguage(language);
    const isActive = language.is_active === 1 || language.is_active === true || language.isActive === true;
    setFormData({
      name: language.name || '',
      code: language.code || '',
      isActive: isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Language name and code are required');
      return;
    }

    try {
      setSaving(true);
      const url = selectedLanguage 
        ? `/admin/content/languages/${selectedLanguage.id}`
        : '/admin/content/languages';
      const method = selectedLanguage ? 'put' : 'post';
      
      const response = await api[method](url, formData);
      if (response.data.success) {
        toast.success(selectedLanguage ? 'Language updated successfully' : 'Language created successfully');
        setShowModal(false);
        fetchLanguages();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save language');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (language) => {
    setSelectedLanguage(language);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await api.delete(`/admin/content/languages/${selectedLanguage.id}`);
      if (response.data.success) {
        toast.success('Language deleted successfully');
        setShowDeleteModal(false);
        fetchLanguages();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete language');
    } finally {
      setDeleting(false);
    }
  };

  const filteredLanguages = languages.filter(language =>
    language.name?.toLowerCase().includes(search.toLowerCase()) ||
    language.code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminSidebar>
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
            Story Languages
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Manage story languages for personalized stories</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
        >
          <Plus className="h-5 w-5" />
          Add Language
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search languages..."
            className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : filteredLanguages.length === 0 ? (
        <EmptyState
          icon={<Globe className="h-7 w-7" />}
          title={search ? `No languages found for "${search}"` : 'No languages yet'}
          description={search ? 'Try adjusting your search terms' : 'Create your first story language to get started'}
          action={search ? null : { label: 'Add Language', onClick: openAddModal }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLanguages.map((language) => (
            <div
              key={language.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{language.name}</h3>
                  <p className="text-sm text-gray-600">Code: {language.code}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    language.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {language.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(language)}
                  className="flex-1 px-3 py-2 text-sm font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                >
                  <Edit className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => openDeleteConfirm(language)}
                  className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal
          title={selectedLanguage ? 'Edit Language' : 'Add Language'}
          onClose={() => setShowModal(false)}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., English, Spanish, French"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase() })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., en, es, fr"
                maxLength="5"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active Status</span>
              <button
                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {formData.isActive ? 'Active' : 'Inactive'}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : selectedLanguage ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && selectedLanguage && (
        <ConfirmDialog
          title="Delete Language"
          description={`Are you sure you want to delete "${selectedLanguage.name}"? This action cannot be undone.`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedLanguage(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminContentLanguages;

